/**
 * The decisions behind the "renseigner un rendez-vous pris" dialog, kept out
 * of the component so they can be tested without a DOM (Kevin, 2026-09-23).
 *
 * Two of them carry the behaviour Kevin asked for by name:
 *   - the most recently called prospect that a meeting can travel on is the
 *     preselected choice (the server returns them most recent first);
 *   - the booking-link button only exists when a link is actually
 *     configured for that client. A button that leads nowhere is never shown.
 */

export interface ProspectOption {
  id: string;
  /** Le client a qui ce prospect appartient. */
  customer_id?: string;
  prospect_name?: string;
  prospect_company?: string;
  prospect_phone?: string;
  last_call_at?: string;
  [key: string]: unknown;
}

/** The client record the dialer already holds (GET /api/customers). */
export interface CustomerCrmView {
  /** The Customer id; the Swoft slot picker needs it to find the host. */
  id?: string | null;
  calendly_url?: string | null;
  active_crm_provider?: string | null;
  swoft_crm_enabled?: boolean | null;
}

/**
 * Une entrée par personne, pas une par ligne de la file.
 *
 * La file d'appel contient plusieurs lignes pour un même numéro, parce qu'un
 * même contact arrive dans plusieurs listes ou qu'un fichier a été importé
 * deux fois. L'écran affichait alors « Alexandre Andre · Vertane » trois fois
 * de suite, sans rien pour les distinguer : choisir devenait un tirage au sort
 * (constaté par Kevin le 2026-09-30).
 *
 * Toutes ces lignes mènent au même appel porteur, donc n'importe laquelle
 * conviendrait ; on garde la PREMIÈRE, c'est-à-dire, après le tri, la plus
 * récemment appelée.
 */
export function dedupeByProspect(prospects: ProspectOption[]): ProspectOption[] {
  const seen = new Set<string>();
  const out: ProspectOption[] = [];
  for (const prospect of prospects) {
    const digits = typeof prospect.prospect_phone === 'string'
      ? prospect.prospect_phone.replace(/\D/g, '')
      : '';
    // Sans numéro, on ne peut rapprocher que par identité : la ligne reste.
    // Per client: the same number under two clients is two contexts.
    const client = typeof prospect.customer_id === 'string' ? prospect.customer_id : '';
    const key = digits ? `${client}|tel:${digits.length > 9 ? digits.slice(-9) : digits}` : `id:${prospect.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(prospect);
  }
  return out;
}

/**
 * The preselected prospect: the most recently called one a meeting can
 * travel on (a finished call), else the head of the list. The list comes
 * from the server, most recently called first.
 */
export function preselectedMeetingProspect<T extends ProspectOption & { has_finished_call?: boolean }>(sorted: T[]): T | null {
  return sorted.find((p) => p.has_finished_call) ?? sorted[0] ?? null;
}

export function prospectLabel(prospect: ProspectOption): string {
  const name = (prospect.prospect_name ?? '').trim();
  return name || (prospect.prospect_phone ?? '').trim() || prospect.id;
}

/** True when this client's meetings are pushed to the Swoft CRM. */
export function hasSwoftCrm(customer: CustomerCrmView | null | undefined): boolean {
  if (!customer) return false;
  return customer.active_crm_provider === 'swoft' && customer.swoft_crm_enabled === true;
}

/**
 * Le client d'un prospect, et non celui qu'on regarde.
 *
 * 🚨 L'ecran « Renseigner un rendez-vous pris » du menu lisait le client
 * selectionne dans la barre laterale. Un appelant qui travaille pour
 * plusieurs clients peut y choisir le prospect d'un autre : le geste, le CRM
 * interroge et le lien ouvert etaient alors ceux du mauvais client. La fiche
 * contact, elle, resolvait deja par le prospect.
 */
export function customerForProspect(
  prospect: ProspectOption | null | undefined,
  customers: CustomerCrmView[] | null | undefined,
  fallback: CustomerCrmView | null = null,
): CustomerCrmView | null {
  const id = prospect?.customer_id;
  if (typeof id !== 'string' || !id) return fallback;
  return (customers || []).find((c) => c.id === id) ?? fallback;
}

/**
 * Ce client a-t-il un CRM branche, quel qu'il soit ?
 *
 * 🚨 C'est ce qui decide du GESTE, pas seulement d'un bouton. Kevin,
 * 2026-09-30 : sans CRM, « Renseigner un rendez-vous pris » n'ouvre aucun
 * formulaire. Il ouvre le lien de prise de rendez-vous et marque le
 * rendez-vous comme obtenu, parce qu'il n'y a ni agenda a interroger ni CRM
 * a qui transmettre une date. Avec un CRM, le calendrier s'ouvre : date,
 * duree, memo, et le rendez-vous est confirme a la validation.
 */
export function hasConnectedCrm(customer: CustomerCrmView | null | undefined): boolean {
  if (!customer) return false;
  if (customer.active_crm_provider === 'swoft') return customer.swoft_crm_enabled === true;
  return customer.active_crm_provider === 'hubspot';
}

/**
 * Le geste attendu quand on renseigne un rendez-vous pris.
 *
 *   'form' : le calendrier, parce qu'un CRM attend une date.
 *   'link' : ouvrir le lien de prise de rendez-vous et cocher « obtenu ».
 *
 * 🚨 `crmReady` est ce que le SERVEUR repond, pas ce que la fiche du client
 * annonce. Un client declare sur Swoft sans connexion vivante voyait
 * s'ouvrir le calendrier, puis un refus : la fiche disait « Swoft », ce qui
 * n'est pas « Swoft repond » (Kevin, 2026-09-30).
 *
 * Sans CRM ET sans lien configure, il ne reste que le formulaire : c'est le
 * seul moyen d'enregistrer quoi que ce soit.
 */
export function bookingGesture(
  customer: CustomerCrmView | null | undefined,
  crmReady: boolean,
): 'form' | 'link' {
  if (crmReady) return 'form';
  return bookingLinkUrl(customer) ? 'link' : 'form';
}

/**
 * Whether the form offers the host's free slots read from Swoft: the client
 * runs the Swoft CRM and is identified, so the server can find its link.
 */
export function offersSwoftSlots(customer: CustomerCrmView | null | undefined): boolean {
  return hasSwoftCrm(customer) && typeof customer?.id === 'string' && customer.id !== '';
}

/**
 * The booking link to offer beside the date, or null when there is none.
 * Null means no button at all, never a disabled or dead one.
 */
export function bookingLinkUrl(customer: CustomerCrmView | null | undefined): string | null {
  const url = typeof customer?.calendly_url === 'string' ? customer.calendly_url.trim() : '';
  return url || null;
}

/**
 * Les refus du calendrier Swoft auxquels reessayer ne sert a rien : c'est la
 * configuration qui manque, pas le reseau qui a hoquete.
 */
const REFUS_DE_CONFIGURATION = ['SWOFT_NOT_CONNECTED', 'SWOFT_NO_MEETING_OWNER'];

export function isSlotConfigurationFailure(code: string): boolean {
  return REFUS_DE_CONFIGURATION.includes(code);
}

/**
 * Faut-il montrer le bouton « Lien de prise de rendez-vous » ?
 *
 * 🚨 Des qu'un lien est configure et que le calendrier Swoft ne peut pas
 * repondre. La regle ne regardait que `active_crm_provider`, donc un client
 * declare sur le CRM Swoft mais sans connexion vivante tombait sur
 * « Le CRM Swoft de ce client n'est pas connecte » et n'avait plus rien :
 * ni creneaux, ni lien, alors que son lien etait renseigne (Kevin,
 * 2026-09-30). Le lien prend le relais dans ce cas.
 *
 * `creneauxUtilisables` est ce que le serveur a REELLEMENT repondu, pas ce
 * que la fiche du client annonce : c'est toute la difference.
 */
export function showsBookingLink(
  customer: CustomerCrmView | null | undefined,
  creneauxUtilisables: boolean = offersSwoftSlots(customer),
): boolean {
  return !creneauxUtilisables && bookingLinkUrl(customer) !== null;
}

// ── The request both screens send ───────────────────────────────────────────

export interface BookedMeetingChoice {
  prospectId: string;
  year: number;
  /** 0-based, as JavaScript counts months. */
  month: number;
  day: number;
  /** "HH" and "MM" as shown in the two pickers. */
  hour: string;
  minute: string;
  durationMinutes: number;
  notes?: string;
  /** The call the meeting was obtained on (call page, spec need b). */
  parallelCallId?: string | null;
}

export interface BookedMeetingRequestBody {
  prospect_id: string;
  meeting_at: string;
  meeting_time: string;
  duration_minutes: number;
  notes: string;
  parallel_call_id?: string;
}

/**
 * The body posted to /integrations/meetings/booked.
 *
 * The prospect card and the left-nav entry both call this: the only thing
 * that differs between them is where `prospectId` came from, so the same
 * prospect and the same date must produce byte-identical requests. That is
 * what the test asserts, and what keeps the two screens from drifting.
 */
export function buildBookedMeetingRequest(choice: BookedMeetingChoice): BookedMeetingRequestBody {
  const at = new Date(choice.year, choice.month, choice.day, Number(choice.hour), Number(choice.minute), 0, 0);
  return {
    prospect_id: choice.prospectId,
    meeting_at: at.toISOString(),
    meeting_time: `${String(choice.hour).padStart(2, '0')}:${String(choice.minute).padStart(2, '0')}`,
    duration_minutes: choice.durationMinutes,
    notes: choice.notes ?? '',
    ...(choice.parallelCallId ? { parallel_call_id: choice.parallelCallId } : {}),
  };
}

/** What the caller is told, from what the server says it did. */
export type BookedMeetingFeedbackKey = 'savedAndSent' | 'savedNoCrm' | 'savedNoCall';

export function feedbackKeyFor(crmOutcome: 'sent' | 'saved_only_no_crm' | 'saved_only_no_call'): BookedMeetingFeedbackKey {
  if (crmOutcome === 'sent') return 'savedAndSent';
  if (crmOutcome === 'saved_only_no_call') return 'savedNoCall';
  return 'savedNoCrm';
}
